import { useState, useRef, useCallback } from 'react'

export const REEL_LABELS = {
  0:   { label: 'Unrated',             color: '#3a3a5a' },
  0.5: { label: 'Dead Channel 📵',     color: '#6b7280' },
  1:   { label: 'Buffering… ⏳',       color: '#8a7a6a' },
  1.5: { label: 'Low Signal 📶',       color: '#9a8a6a' },
  2:   { label: 'Standard Def 📼',     color: '#b09060' },
  2.5: { label: 'Worth Streaming 📺',  color: '#c09850' },
  3:   { label: 'Good Reception 📡',   color: '#d0a840' },
  3.5: { label: 'On Your List 📝',     color: '#e0b430' },
  4:   { label: 'Binge Alert 🍿',      color: '#f0b429' },
  4.5: { label: 'Season Finale ⚡',    color: '#f5c020' },
  5:   { label: 'All-Timer 🎬',        color: '#ffd700' },
}

// Each tier: how much the reel spins + how the scale bounces + stagger between reels
function getTier(rating) {
  if (rating <= 1)  return { id:1, spin:'rrSpin1', dur:750,  stagger:0,   scale:'rrScale1', labelAnim:'rrLabelFade',   labelDur:650  }
  if (rating <= 2)  return { id:2, spin:'rrSpin2', dur:650,  stagger:35,  scale:'rrScale2', labelAnim:'rrLabelSlide',  labelDur:500  }
  if (rating <= 3)  return { id:3, spin:'rrSpin3', dur:750,  stagger:55,  scale:'rrScale3', labelAnim:'rrLabelBounce', labelDur:550  }
  if (rating <= 4)  return { id:4, spin:'rrSpin4', dur:800,  stagger:65,  scale:'rrScale4', labelAnim:'rrLabelPop',    labelDur:520  }
  if (rating < 5)   return { id:5, spin:'rrSpin5', dur:900,  stagger:55,  scale:'rrScale5', labelAnim:'rrLabelZoom',   labelDur:500  }
  return              { id:6, spin:'rrSpin6', dur:1050, stagger:40,  scale:'rrScale6', labelAnim:'rrLabelZoom',   labelDur:500  }
}

// Stable per-slot clip path IDs — never change during hover
const CLIP_IDS = Array.from({ length:5 }, (_,i) => `rr_clip_${i}_${Math.random().toString(36).slice(2,7)}`)

// ── Single reel icon ──────────────────────────────────────────────────────────
// Exported so other half-increment rating pickers (HomeDeck's own hold-to-rate
// gesture) can reuse the real partial-fill glyph instead of inventing a second
// one — the fanned-icon animation around it is HomeDeck's own, this just draws
// the reel with a given fillFraction (0 / 0.5 / 1).
export function ReelIcon({ n, size, fillFraction, color, burstKey = 0, tier = { id: 1, stagger: 0 } }) {
  const id  = CLIP_IDS[n - 1]
  const dim = 'rgba(var(--ink-rgb),0.15)'
  const spokes = [0,60,120,180,240,300].map(deg => {
    const r = deg * Math.PI / 180
    return {
      x1: 12+Math.cos(r)*4.2, y1: 12+Math.sin(r)*4.2,
      x2: 12+Math.cos(r)*8.5, y2: 12+Math.sin(r)*8.5,
      hx: 12+Math.cos(r)*7,   hy: 12+Math.sin(r)*7,
    }
  })

  // Tier 1: no stagger — they all droop together
  const delay  = tier.stagger > 0 ? (n - 1) * tier.stagger : 0
  // Glow on high tiers when burst active
  const glow   = tier.id >= 6 ? `drop-shadow(0 0 10px ${color}cc)`
               : tier.id >= 5 ? `drop-shadow(0 0 6px ${color}99)`
               : tier.id >= 4 ? `drop-shadow(0 0 4px ${color}66)`
               : 'none'

  return (
    // key = burstKey → React remounts this wrapper on each rating, restarting the animation cleanly
    <div key={burstKey} style={{
      display: 'flex',
      '--nxa': burstKey > 0
        ? `${tier.scale} ${tier.dur}ms ${delay}ms var(--ease-pop) both`
        : 'none',
    }}>
      <svg
        key={`svg_${burstKey}`}
        width={size} height={size} viewBox="0 0 24 24" fill="none"
        style={{
          display: 'block', flexShrink: 0,
          filter: burstKey > 0 && fillFraction > 0 ? glow : 'none',
          // The reel itself spins — this is the money animation
          '--nxa': burstKey > 0 && fillFraction > 0
            ? `${tier.spin} ${tier.dur}ms ${delay}ms cubic-bezier(.34,1.2,.64,1) both`
            : 'none',
        }}>
        <defs>
          {fillFraction > 0 && fillFraction < 1 && (
            <clipPath id={id}><rect x="0" y="0" width={24*fillFraction} height="24"/></clipPath>
          )}
        </defs>
        <g stroke={dim} strokeWidth="1.5" fill="none">
          <circle cx="12" cy="12" r="10"/>
          <circle cx="12" cy="12" r="3"/>
          {spokes.map((s,i) => (
            <g key={i}>
              <line x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} strokeLinecap="round"/>
              <circle cx={s.hx} cy={s.hy} r="1.1"/>
            </g>
          ))}
        </g>
        {/* Filled (rated) reels get the same color-mix() treatment as FilmReel/
            Clapboard — a procedural light/dark split per facet off the single
            tier color instead of a flat fill, so a rated reel reads as the same
            "premium metal" object at every tier, not just a filled-in outline. */}
        {fillFraction > 0 && (
          <g clipPath={fillFraction < 1 ? `url(#${id})` : undefined} style={{ '--ic': color }}>
            <circle cx="12" cy="12" r="10" fill="color-mix(in srgb, var(--ic) 16%, transparent)" stroke="color-mix(in srgb, var(--ic) 88%, black 12%)" strokeWidth="1.5"/>
            <circle cx="12" cy="12" r="3" fill="color-mix(in srgb, var(--ic) 92%, white 8%)" stroke="color-mix(in srgb, var(--ic) 80%, black 20%)" strokeWidth="1.5"/>
            {spokes.map((s,i) => (
              <g key={i} stroke="color-mix(in srgb, var(--ic) 75%, black 10%)" strokeWidth="1.5">
                <line x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} strokeLinecap="round"/>
                <circle cx={s.hx} cy={s.hy} r="1.1" fill="color-mix(in srgb, var(--ic) 85%, white 15%)"/>
              </g>
            ))}
          </g>
        )}
      </svg>
    </div>
  )
}

// ── Interactive rating ────────────────────────────────────────────────────────
export function ReelRating({ value = 0, onChange, size = 36, showLabel = true, readonly = false }) {
  const [hover,      setHover]      = useState(null)
  const [burstKey,   setBurstKey]   = useState(0)
  const [burstTier,  setBurstTier]  = useState(() => getTier(0))
  const [flashKey,   setFlashKey]   = useState(0)
  const [labelKey,   setLabelKey]   = useState(0)
  const containerRef = useRef(null)
  const dragging     = useRef(false)
  const lastHover    = useRef(null)   // track last snapped value to skip redundant setState

  const display  = hover ?? value
  const rKey     = Math.round(display * 2) / 2
  const cfg      = REEL_LABELS[rKey] || REEL_LABELS[0]
  const isAllTimer = value === 5 && !hover

  const posToRating = useCallback((clientX) => {
    if (!containerRef.current) return 1
    const rect = containerRef.current.getBoundingClientRect()
    const pct  = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
    // Even half-steps across the FULL width — every half-reel gets an equal ~10%
    // band, so halves are exactly as easy to hit as wholes. (Was: an asymmetric
    // 40/60 split inside each reel, which made the half-target a tiny ~20px sliver
    // on mobile.) The live drag preview + label make fine-tuning forgiving.
    return Math.max(0.5, Math.round(pct * 10) / 2)
  }, [])

  const commit = useCallback((rating) => {
    // NEVER auto-clear: re-tapping your current rating used to wipe it to 0
    // (silent data loss). Clearing is now a deliberate, separate control.
    const next = rating
    onChange?.(next)
    setHover(null)
    if (next > 0) {
      const t = getTier(next)
      setBurstTier(t)
      setBurstKey(k => k + 1)
      setLabelKey(k => k + 1)
      if (t.id >= 4) setFlashKey(k => k + 1)
      // Haptic feedback on supported devices (iOS PWA, Android Chrome)
      if (navigator.vibrate) {
        navigator.vibrate(t.id >= 5 ? [15, 30, 20] : t.id >= 4 ? [12] : [8])
      }
    }
  }, [value, onChange])

  const onMouseMove  = (e) => {
    if (readonly) return
    const v = posToRating(e.clientX)
    if (v !== lastHover.current) { lastHover.current = v; setHover(v) }
  }
  const onMouseLeave = ()  => { if (!readonly) { lastHover.current = null; setHover(null) } }
  const onClick      = (e) => { if (!readonly) commit(posToRating(e.clientX)) }
  const onTouchStart = (e) => {
    if (readonly) return
    dragging.current = true
    const v = posToRating(e.touches[0].clientX)
    lastHover.current = v
    setHover(v)
  }
  const onTouchMove = (e) => {
    if (!dragging.current || readonly) return
    e.preventDefault()
    const v = posToRating(e.touches[0].clientX)
    if (v !== lastHover.current) { lastHover.current = v; setHover(v) }
  }
  const onTouchEnd = (e) => {
    if (!dragging.current || readonly) return
    dragging.current = false
    const finalVal = lastHover.current  // capture BEFORE clearing, ref is always current, state may be stale
    lastHover.current = null
    setHover(null)
    if (finalVal !== null) {
      e.preventDefault()  // prevent the synthetic click event iOS fires after touchend
      commit(finalVal)
    }
  }

  // Keyboard control — this is a role="slider", so it must be operable without a
  // pointer. Arrows step by half a reel, Home/End jump to the ends, Delete clears.
  // Before this, keyboard and switch users could not rate at all (2026-07-06 a11y audit).
  const clearRating = () => { onChange?.(0); setHover(null) }
  const onKeyDown = (e) => {
    if (readonly) return
    const cur = value || 0
    switch (e.key) {
      case 'ArrowRight': case 'ArrowUp':   commit(Math.min(5, cur + 0.5)); break
      case 'ArrowLeft':  case 'ArrowDown': { const nv = Math.max(0, cur - 0.5); nv === 0 ? clearRating() : commit(nv); break }
      case 'Home':       commit(0.5); break
      case 'End':        commit(5); break
      case 'Delete': case 'Backspace': clearRating(); break
      default: return
    }
    e.preventDefault()
  }
  const valueLabel = value > 0 ? (REEL_LABELS[Math.round(value * 2) / 2]?.label || `${value} of 5`) : 'Unrated'

  return (
    <div style={{ display:'flex', flexDirection:'column', gap: showLabel ? 10 : 0, position:'relative' }}>

      {/* Ambient flash for tier 4+, remounts on flashKey */}
      {flashKey > 0 && (
        <div key={flashKey} style={{
          position:'absolute', inset:'-18px', borderRadius:22, pointerEvents:'none', zIndex:0,
          background: value === 5
            ? 'radial-gradient(ellipse at 50% 60%, rgba(255,215,0,0.24) 0%, transparent 65%)'
            : value >= 4.5
              ? 'radial-gradient(ellipse at 50% 60%, rgba(245,192,32,0.16) 0%, transparent 65%)'
              : 'radial-gradient(ellipse at 50% 60%, rgba(240,180,41,0.10) 0%, transparent 65%)',
          '--nxa': 'rrFlash 700ms ease-out forwards',
        }}/>
      )}

      {/* All-Timer spotlight sweep */}
      {burstKey > 0 && burstTier.id >= 6 && (
        <div key={`spot_${burstKey}`} style={{
          position:'absolute', top:0, bottom:0, left:0,
          width:'55%', pointerEvents:'none', zIndex:3,
          background:'linear-gradient(90deg, transparent 0%, rgba(255,215,0,0.28) 50%, transparent 100%)',
          '--nxa': 'rrSpotlight 700ms 150ms ease-in-out forwards',
          borderRadius:'var(--radius-xs)',
        }}/>
      )}

      {/* Reel row, single gesture area */}
      <div
        ref={containerRef}
        role={readonly ? undefined : 'slider'}
        tabIndex={readonly ? undefined : 0}
        aria-label={readonly ? undefined : 'Rate this show'}
        aria-valuemin={readonly ? undefined : 0}
        aria-valuemax={readonly ? undefined : 5}
        aria-valuenow={readonly ? undefined : value}
        aria-valuetext={readonly ? undefined : valueLabel}
        onKeyDown={onKeyDown}
        style={{
          display:'flex', alignItems:'center', justifyContent:'space-between',
          gap:6, cursor: readonly ? 'default' : 'pointer',
          touchAction:'none', userSelect:'none', outlineOffset:3,
          position:'relative', zIndex:1, padding:'10px 0',
        }}
        onMouseMove={onMouseMove} onMouseLeave={onMouseLeave} onClick={onClick}
        onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
      >
        {[1,2,3,4,5].map(n => {
          const fill = display >= n ? 1 : display >= n - 0.5 ? 0.5 : 0
          const isActive = !readonly && hover !== null && Math.ceil(Math.max(hover, 0.01)) === n
          return (
            <div key={n} style={{
              flex:1, display:'flex', justifyContent:'center', alignItems:'center',
              // Hover preview — subtle scale toward the hovered tier's scale
              transform: isActive && !readonly
                ? `scale(${0.95 + getTier(display).id * 0.025})`
                : 'scale(1)',
              transition:'transform 0.13s var(--ease-pop)',
            }}>
              <ReelIcon
                n={n}
                size={size}
                fillFraction={fill}
                color={cfg.color}
                burstKey={fill > 0 ? burstKey : 0}
                tier={burstTier}
              />
            </div>
          )
        })}
      </div>

      {/* Label + deliberate Clear */}
      {showLabel && (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, position:'relative', zIndex:1 }}>
          <div key={labelKey} style={{
            fontSize:13, fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase',
            color: display > 0 ? cfg.color : 'var(--text-dim)',
            minHeight:18,
            backgroundImage: isAllTimer
              ? 'linear-gradient(90deg, #ffd700, #ffec6e, #ffd700, #e8a900, #ffd700)' : 'none',
            backgroundSize:       isAllTimer ? '200% 100%' : undefined,
            WebkitBackgroundClip: isAllTimer ? 'text'      : undefined,
            WebkitTextFillColor:  isAllTimer ? 'transparent' : undefined,
            '--nxa': isAllTimer
              ? 'rrShimmer 2.5s linear infinite'
              : labelKey > 0
                ? `${burstTier.labelAnim} ${burstTier.labelDur}ms var(--ease-pop) both`
                : 'none',
          }}>
            {display > 0 ? <><span style={{ fontFamily:'var(--font-mono)', fontWeight:600, opacity:0.75, marginRight:8 }}>{rKey.toFixed(1)}</span>{cfg.label}</> : readonly ? '—' : 'Tap or drag to rate'}
          </div>
          {!readonly && value > 0 && (
            <button onClick={(e) => { e.stopPropagation(); onChange?.(0); setHover(null) }}
              style={{ background:'none', border:'none', color:'var(--text-dim)', fontSize:11, fontWeight:600, cursor:'pointer', fontFamily:'var(--font)', padding:'2px 6px', flexShrink:0, letterSpacing:'0.03em' }}>
              Clear
            </button>
          )}
        </div>
      )}

      <style>{`
        /* ── Reel spin animations — rotation amount scales with rating ── */

        /* Tier 1: barely twitches — 30° only, sluggish */
        @keyframes rrSpin1 {
          0%   { transform: rotate(0deg);   opacity:0.5 }
          60%  { transform: rotate(35deg);  opacity:0.9 }
          100% { transform: rotate(30deg);  opacity:1   }
        }
        /* Tier 2: quarter turn — polite */
        @keyframes rrSpin2 {
          0%   { transform: rotate(0deg) }
          65%  { transform: rotate(105deg) }
          100% { transform: rotate(90deg) }
        }
        /* Tier 3: full turn — proper momentum */
        @keyframes rrSpin3 {
          0%   { transform: rotate(0deg) }
          62%  { transform: rotate(390deg) }
          82%  { transform: rotate(350deg) }
          100% { transform: rotate(360deg) }
        }
        /* Tier 4: one and a half turns — energetic snap */
        @keyframes rrSpin4 {
          0%   { transform: rotate(0deg) }
          58%  { transform: rotate(580deg) }
          78%  { transform: rotate(520deg) }
          92%  { transform: rotate(545deg) }
          100% { transform: rotate(540deg) }
        }
        /* Tier 5: two turns — exciting, fast */
        @keyframes rrSpin5 {
          0%   { transform: rotate(0deg) }
          55%  { transform: rotate(760deg) }
          74%  { transform: rotate(700deg) }
          88%  { transform: rotate(725deg) }
          100% { transform: rotate(720deg) }
        }
        /* Tier 6: two turns, punchy — the All-Timer */
        @keyframes rrSpin6 {
          0%   { transform: rotate(0deg) }
          50%  { transform: rotate(780deg) }
          68%  { transform: rotate(705deg) }
          82%  { transform: rotate(730deg) }
          92%  { transform: rotate(718deg) }
          100% { transform: rotate(720deg) }
        }

        /* ── Scale bounce — wrapper, separate from spin so they compound ── */
        @keyframes rrScale1 {
          0%   { transform: scale(1.03); opacity:0.45 }
          45%  { transform: scale(0.96); opacity:0.8  }
          100% { transform: scale(1);   opacity:1    }
        }
        @keyframes rrScale2 {
          0%   { transform: scale(0.92); opacity:0.6 }
          60%  { transform: scale(1.08) }
          100% { transform: scale(1);   opacity:1   }
        }
        @keyframes rrScale3 {
          0%   { transform: scale(0.84); opacity:0.55 }
          55%  { transform: scale(1.14) }
          80%  { transform: scale(0.98) }
          100% { transform: scale(1);   opacity:1   }
        }
        @keyframes rrScale4 {
          0%   { transform: scale(0.70); opacity:0.45 }
          52%  { transform: scale(1.20) }
          74%  { transform: scale(0.96) }
          90%  { transform: scale(1.03) }
          100% { transform: scale(1);   opacity:1   }
        }
        @keyframes rrScale5 {
          0%   { transform: scale(0.58); opacity:0.4 }
          50%  { transform: scale(1.26) }
          70%  { transform: scale(0.94) }
          85%  { transform: scale(1.06) }
          95%  { transform: scale(0.99) }
          100% { transform: scale(1);   opacity:1   }
        }
        @keyframes rrScale6 {
          0%   { transform: scale(0.50); opacity:0.35 }
          48%  { transform: scale(1.32) }
          66%  { transform: scale(0.92) }
          80%  { transform: scale(1.08) }
          91%  { transform: scale(0.98) }
          97%  { transform: scale(1.02) }
          100% { transform: scale(1);   opacity:1   }
        }

        /* ── Label animations ── */
        @keyframes rrLabelFade {
          from { opacity:0 } to { opacity:1 }
        }
        @keyframes rrLabelSlide {
          from { opacity:0; transform:translateY(8px) } to { opacity:1; transform:translateY(0) }
        }
        @keyframes rrLabelBounce {
          0%   { transform:scale(0.8) translateY(5px); opacity:0 }
          65%  { transform:scale(1.07) }
          100% { transform:scale(1) translateY(0);    opacity:1 }
        }
        @keyframes rrLabelPop {
          0%   { transform:scale(0.62) translateY(7px); opacity:0 }
          58%  { transform:scale(1.16) }
          80%  { transform:scale(0.96) }
          100% { transform:scale(1) translateY(0);    opacity:1 }
        }
        @keyframes rrLabelZoom {
          0%   { transform:scale(0.45) translateY(9px); opacity:0 }
          52%  { transform:scale(1.24) }
          72%  { transform:scale(0.93) }
          87%  { transform:scale(1.06) }
          100% { transform:scale(1) translateY(0);    opacity:1 }
        }

        /* ── Ambient flash ── */
        @keyframes rrFlash {
          0%   { opacity:0 }
          22%  { opacity:1 }
          100% { opacity:0 }
        }

        /* ── All-Timer spotlight sweep ── */
        @keyframes rrSpotlight {
          from { transform:translateX(-110%) }
          to   { transform:translateX(280%)  }
        }

        /* ── All-Timer label shimmer ── */
        @keyframes rrShimmer {
          0%   { background-position: 200% 0 }
          100% { background-position:-200% 0 }
        }
      `}</style>
    </div>
  )
}

// ── ReelMark — one solid reel, used as a plotted point on the Reading Room's
// trajectory chart (a take's rating over time). Colored by its own rating value so
// it reads on the same scale as the reels everywhere else; `glow` lights the newest
// (current) verdict. (Jason 2026-07-23, Reading Room redesign.)
export function ReelMark({ value = 4, size = 22, glow = false }) {
  const k = Math.round(value * 2) / 2
  const color = (REEL_LABELS[k] || REEL_LABELS[4]).color
  const spokes = [0,60,120,180,240,300].map(deg => {
    const r = deg * Math.PI / 180
    return { x1:12+Math.cos(r)*4.2, y1:12+Math.sin(r)*4.2, x2:12+Math.cos(r)*8.5, y2:12+Math.sin(r)*8.5, hx:12+Math.cos(r)*7, hy:12+Math.sin(r)*7 }
  })
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      style={{ display:'block', filter: glow ? `drop-shadow(0 0 6px ${color})` : 'none', '--ic': color }}>
      <circle cx="12" cy="12" r="11" fill="var(--bg-card)" />
      <circle cx="12" cy="12" r="10" fill="color-mix(in srgb, var(--ic) 16%, transparent)" stroke="color-mix(in srgb, var(--ic) 88%, black 12%)" strokeWidth="1.6" />
      <circle cx="12" cy="12" r="3" fill="color-mix(in srgb, var(--ic) 92%, white 8%)" stroke="color-mix(in srgb, var(--ic) 80%, black 20%)" strokeWidth="1.6" />
      {spokes.map((s,i) => (
        <g key={i} stroke="color-mix(in srgb, var(--ic) 75%, black 10%)" strokeWidth="1.6">
          <line x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} strokeLinecap="round" />
          <circle cx={s.hx} cy={s.hy} r="1.15" fill="color-mix(in srgb, var(--ic) 85%, white 15%)" />
        </g>
      ))}
    </svg>
  )
}

// ── Static display — used across cards, notifications, lists. Unchanged. ──────
// showEmpty: render all 5 (dim, unfilled) reels even at value=0, instead of nothing —
// for a surface where the reels themselves are the persistent "this is rateable"
// identity (SeasonEpisodes' below-episode row, Jason 2026-08-15), not just a badge
// that appears once rated. Every other call site keeps the original silent-when-
// unrated behavior; the per-reel render logic already handles fill=0 correctly
// (dim outline only), so this only changes whether the early return fires.
export function ReelDisplay({ value = 0, size = 14, showEmpty = false }) {
  if (!value && !showEmpty) return null
  const k   = Math.round(value * 2) / 2
  const cfg = REEL_LABELS[k] || REEL_LABELS[0]
  return (
    <div style={{ display:'flex', gap:2, alignItems:'center' }}>
      {[1,2,3,4,5].map(n => {
        const fill = value >= n ? 1 : value >= n - 0.5 ? 0.5 : 0
        const id   = `rdisp_${n}_${k}`
        const dim  = 'rgba(var(--ink-rgb),0.18)'
        const spokes = [0,60,120,180,240,300].map(deg => {
          const r = deg * Math.PI / 180
          return { x1:12+Math.cos(r)*4.2, y1:12+Math.sin(r)*4.2, x2:12+Math.cos(r)*8.5, y2:12+Math.sin(r)*8.5, hx:12+Math.cos(r)*7, hy:12+Math.sin(r)*7 }
        })
        return (
          <svg key={n} width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ flexShrink:0 }}>
            <defs>{fill>0&&fill<1&&<clipPath id={id}><rect x="0" y="0" width={24*fill} height="24"/></clipPath>}</defs>
            <g stroke={dim} strokeWidth="1.5" fill="none">
              <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3"/>
              {spokes.map((s,i)=><g key={i}><line x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} strokeLinecap="round"/><circle cx={s.hx} cy={s.hy} r="1.1"/></g>)}
            </g>
            {fill>0&&<g stroke={cfg.color} strokeWidth="1.5" fill={cfg.color} clipPath={fill<1?`url(#${id})`:undefined}>
              <circle cx="12" cy="12" r="10" fillOpacity="0.12"/><circle cx="12" cy="12" r="3" fillOpacity="0.5"/>
              {spokes.map((s,i)=><g key={i}><line x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} strokeLinecap="round"/><circle cx={s.hx} cy={s.hy} r="1.1"/></g>)}
            </g>}
          </svg>
        )
      })}
    </div>
  )
}
